import React, { useContext } from 'react';
import classnames from 'classnames';
import { PapiGallection } from '@wix/da-papi-types';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import { BiContextProvidedValue, BiData, ItemClickBiEvent } from '@wix/da-bi/pkg/events';
import BiDiv from '@wix/da-bi/pkg/Components/BiDiv';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import ConditionalWrapper from '@wix/da-ds/pkg/ConditionalWrapper';
import { gallectionTypeToResourceType } from '../../../types/gallection';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import folderCardStyles from '../../FolderCard.scss';

export type Props = {
  folder: PapiGallection;
  width: number;
  height: number;
  className?: string;
  contentClickBiData?: ItemClickBiEvent;
  dataHook?: string;
  content: React.ReactNode;
  ribbon?: React.ReactNode;
  footer: React.ReactNode;
  wrapCondition?: boolean;
  wrap?: (child: React.ReactNode) => React.ReactNode;
  isSelected?: boolean;
  isParent?: boolean;
  onClick?: (e) => void;
  disableMarginRight?: boolean;
};

export const FolderCardLayout: React.FC<React.PropsWithChildren<Props>> = ({
  folder,
  className,
  contentClickBiData,
  dataHook,
  content,
  ribbon,
  footer,
  wrapCondition,
  wrap,
  isSelected,
  isParent,
  onClick,
  width,
  height,
  disableMarginRight,
}) => {
  const isMobile = useContext(MobileContext);
  return (
    <ErrorBoundary debugComponent="FolderCardLayout">
      <Section
        className={classnames(
          folderCardStyles['root'],
          onClick && folderCardStyles['clickable'],
          isParent && folderCardStyles['parentFolder'],
          isMobile && folderCardStyles['mobile'],
          disableMarginRight && folderCardStyles['no-margin-right'],
          className
        )}
        style={{ width, height }}
      >
        <ConditionalWrapper condition={wrapCondition} wrap={wrap}>
          <div className={folderCardStyles['wrapper']}>
            <BiDiv
              className={classnames(
                folderCardStyles['content'],
                'ds-card',
                isSelected && 'ds-card-selected'
              )}
              onClick={onClick}
              biData={BiData<ItemClickBiEvent>({
                evid: 100,
                page_typeid: BiContextProvidedValue,
                page_itemid: BiContextProvidedValue,
                typeid: gallectionTypeToResourceType(folder.type),
                itemid: BiContextProvidedValue,
                rank: BiContextProvidedValue,
                sectionname: BiContextProvidedValue,
                widgetname: BiContextProvidedValue,
                ...contentClickBiData,
              })}
              data-hook={dataHook}
            >
              <div className={folderCardStyles['preview-container']}>{content}</div>
              <div
                className={classnames(
                  folderCardStyles['footer'],
                  isMobile && folderCardStyles['mobileFooter']
                )}
              >
                {footer}
              </div>
            </BiDiv>
            {ribbon && <div className={folderCardStyles['ribbon-wrapper']}>{ribbon}</div>}
          </div>
        </ConditionalWrapper>
      </Section>
    </ErrorBoundary>
  );
};

export default FolderCardLayout;
